import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Pencil,
  Trash2,
  Star,
  StarOff,
  Upload,
  Image as ImageIcon,
  X,
  Plus,
  Save,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Link as LinkIcon,
  UserPlus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
};

const Studio = ({ token, clients, reloadClients }) => {
  const [openSlug, setOpenSlug] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const toggleFeatured = async (c) => {
    try {
      await axios.patch(`${API}/admin/clients/${c.slug}`, { featured: !c.featured }, { headers });
      toast.success(`${c.name} ${c.featured ? "unfeatured" : "featured"}`);
      reloadClients();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const moveClient = async (index, direction) => {
    const swapWith = index + direction;
    if (swapWith < 0 || swapWith >= clients.length) return;
    const a = clients[index];
    const b = clients[swapWith];
    // Assign explicit swapped orders (fall back to array index if order is missing)
    const orderA = typeof a.order === "number" ? a.order : index;
    const orderB = typeof b.order === "number" ? b.order : swapWith;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/clients/${a.slug}`, { order: orderB }, { headers }),
        axios.patch(`${API}/admin/clients/${b.slug}`, { order: orderA }, { headers }),
      ]);
      toast.success(`Moved ${a.name} ${direction < 0 ? "up" : "down"}`);
      reloadClients();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const persistNewOrder = async (arranged) => {
    // Patch only clients whose position (index) changed
    const changes = arranged
      .map((c, i) => ({ slug: c.slug, order: i, prev: c.order }))
      .filter((x) => x.prev !== x.order);
    if (changes.length === 0) return;
    try {
      await Promise.all(
        changes.map((x) =>
          axios.patch(`${API}/admin/clients/${x.slug}`, { order: x.order }, { headers })
        )
      );
      toast.success("Order updated");
      reloadClients();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragOverIdx !== i) setDragOverIdx(i);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) {
      handleDragEnd();
      return;
    }
    const next = [...clients];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    handleDragEnd();
    persistNewOrder(next);
  };

  const handleDeleteClient = async () => {
    if (!confirmDelete) return;
    const { type, slug, projectId } = confirmDelete;
    setConfirmDelete(null);
    try {
      if (type === "client") {
        await axios.delete(`${API}/admin/clients/${slug}`, { headers });
        toast.success("Client deleted");
      } else if (type === "project") {
        await axios.delete(`${API}/admin/clients/${slug}/projects/${projectId}`, { headers });
        toast.success("Project deleted");
      }
      reloadClients();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="font-mono-label text-[#F62493] mb-3">Content Studio</p>
          <h2 className="font-display text-3xl md:text-4xl text-stone-100 leading-tight">
            Edit your portfolio.
          </h2>
          <p className="text-stone-200 mt-3 max-w-2xl">
            Click a client to edit its text, swap the cover image, add or remove project images, and toggle whether it appears on the homepage.
          </p>
          <p className="font-mono-label text-stone-300 mt-2 flex items-center gap-2">
            <GripVertical size={12} /> Drag the handle to reorder clients — new order saves automatically.
          </p>
        </div>
        <button
          onClick={() => setShowNewClient(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-5 py-2.5 hover:bg-[#F62493] transition-colors font-mono-label text-xs shrink-0"
        >
          <UserPlus size={14} /> New Client
        </button>
      </div>

      <div className="space-y-4">
        {clients.map((c, i) => (
          <ClientRow
            key={c.slug}
            client={c}
            index={i}
            total={clients.length}
            isOpen={openSlug === c.slug}
            isDragging={dragIdx === i}
            isDragOver={dragOverIdx === i && dragIdx !== null && dragIdx !== i}
            dropPosition={
              dragOverIdx === i && dragIdx !== null && dragIdx !== i
                ? (dragIdx < i ? "after" : "before")
                : null
            }
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={() => handleDrop(i)}
            onDragEnd={handleDragEnd}
            onToggle={() => setOpenSlug(openSlug === c.slug ? null : c.slug)}
            onToggleFeatured={() => toggleFeatured(c)}
            onMove={(dir) => moveClient(i, dir)}
            onAskDelete={(payload) => setConfirmDelete(payload)}
            token={token}
            reload={reloadClients}
          />
        ))}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.type === "client" ? "Delete this client?" : "Delete this image?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.type === "client"
                ? `This will permanently remove "${confirmDelete?.name}" and all of its projects. Cannot be undone.`
                : "The image will be removed from the gallery. Cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewClientDialog
        open={showNewClient}
        onOpenChange={setShowNewClient}
        token={token}
        onCreated={(newClient) => {
          setShowNewClient(false);
          reloadClients();
          setOpenSlug(newClient?.slug || null);
        }}
      />
    </div>
  );
};

// ---------- Single client row ----------

const ClientRow = ({
  client,
  index,
  total,
  isOpen,
  isDragging,
  isDragOver,
  dropPosition,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onToggle,
  onToggleFeatured,
  onMove,
  onAskDelete,
  token,
  reload,
}) => {
  const [draft, setDraft] = useState({
    name: client.name,
    industry: client.industry,
    summary: client.summary,
    before: client.before || "",
    after: client.after || "",
    metrics: (client.metrics || []).join(" · "),
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setDraft({
      name: client.name,
      industry: client.industry,
      summary: client.summary,
      before: client.before || "",
      after: client.after || "",
      metrics: (client.metrics || []).join(" · "),
    });
  }, [client.name, client.industry, client.summary, client.before, client.after, client.metrics]);

  const isDirty =
    draft.name !== client.name ||
    draft.industry !== client.industry ||
    draft.summary !== client.summary ||
    draft.before !== (client.before || "") ||
    draft.after !== (client.after || "") ||
    draft.metrics !== (client.metrics || []).join(" · ");

  const saveMeta = async () => {
    setSavingMeta(true);
    // Parse metrics: split on "·", "|", or newline, trim, drop empties
    const metricsArr = draft.metrics
      .split(/[·|\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await axios.patch(
        `${API}/admin/clients/${client.slug}`,
        { ...draft, metrics: metricsArr },
        { headers }
      );
      toast.success("Saved");
      reload();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative border rounded-sm overflow-hidden transition-all ${
        isDragging
          ? "border-[#F62493] bg-[#141414] opacity-60"
          : "border-stone-800 bg-[#0A0A0A]"
      }`}
      data-testid={`client-row-${client.slug}`}
    >
      {isDragOver && dropPosition === "before" && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#F62493] z-10" />
      )}
      {isDragOver && dropPosition === "after" && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F62493] z-10" />
      )}
      <div className="w-full flex items-center gap-3 px-3 py-4 hover:bg-[#1A1A1A] transition-colors text-left">
        <div
          draggable
          onDragStart={onDragStart}
          role="button"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          className="cursor-grab active:cursor-grabbing p-1 text-stone-300 hover:text-[#F62493] shrink-0"
          data-testid={`drag-handle-${client.slug}`}
        >
          <GripVertical size={18} />
        </div>
        <div className="flex flex-col gap-0.5 shrink-0" data-testid={`reorder-${client.slug}`}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            disabled={index === 0}
            aria-label="Move client up"
            className="p-1 rounded text-stone-300 hover:text-[#F62493] hover:bg-[#242424] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            disabled={index === total - 1}
            aria-label="Move client down"
            className="p-1 rounded text-stone-300 hover:text-[#F62493] hover:bg-[#242424] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowDown size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-4 text-left"
        >
          <div className="w-14 h-14 overflow-hidden bg-[#242424] flex-shrink-0">
            {client.cover ? (
              <img src={resolveImg(client.cover)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-300">
                <ImageIcon size={18} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg text-stone-100 truncate">{client.name}</h3>
              {client.featured && (
                <span className="font-mono-label text-[10px] px-2 py-0.5 bg-[#F62493] text-[#F7F3ED] rounded-full">
                  Featured
                </span>
              )}
            </div>
            <p className="font-mono-label text-stone-300 mt-1">
              {client.industry} · {(client.projects || []).length} pieces
            </p>
          </div>
          <span className="text-stone-300">
            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </span>
        </button>
      </div>

      {isOpen && (
        <div className="px-5 pb-6 pt-2 border-t border-stone-800 bg-[#0F0F0F] space-y-6">
          {/* Featured toggle */}
          <div className="flex items-center justify-between p-4 bg-[#141414] rounded">
            <div>
              <p className="font-display text-stone-100">Show on homepage</p>
              <p className="font-mono-label text-stone-300 mt-1">
                Featured clients appear in the highlighted grid on the homepage.
              </p>
            </div>
            <button
              onClick={onToggleFeatured}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-label text-xs transition-colors ${
                client.featured
                  ? "bg-[#F62493] text-[#F7F3ED]"
                  : "border border-stone-400 text-stone-200 hover:bg-[#1A1A1A]"
              }`}
            >
              {client.featured ? <Star size={14} fill="currentColor" /> : <StarOff size={14} />}
              {client.featured ? "Featured" : "Not featured"}
            </button>
          </div>

          {/* Text fields */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-label text-stone-300">Client name</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="font-mono-label text-stone-300">Industry / tagline</Label>
              <Input
                value={draft.industry}
                onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
                className="mt-1.5"
              />
            </div>
          </div>
          <div>
            <Label className="font-mono-label text-stone-300">Summary</Label>
            <Textarea
              value={draft.summary}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              rows={3}
              className="mt-1.5"
            />
          </div>

          {/* Case Study — Before / After */}
          <div className="pt-4 border-t border-stone-800">
            <p className="font-mono-label text-[#F62493] mb-1">Case Study</p>
            <p className="text-stone-300 text-sm mb-4">
              Add a short before / after story to show impact. Both fields are optional — the section only appears on the client page if you fill in at least one.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono-label text-stone-300">Before — The Challenge</Label>
                <Textarea
                  value={draft.before}
                  onChange={(e) => setDraft({ ...draft, before: e.target.value })}
                  rows={4}
                  placeholder="What was the client facing? What did they need?"
                  className="mt-1.5"
                  data-testid={`case-study-before-${client.slug}`}
                />
              </div>
              <div>
                <Label className="font-mono-label text-stone-300">After — The Impact</Label>
                <Textarea
                  value={draft.after}
                  onChange={(e) => setDraft({ ...draft, after: e.target.value })}
                  rows={4}
                  placeholder="What changed after the work shipped? Numbers, launches, results."
                  className="mt-1.5"
                  data-testid={`case-study-after-${client.slug}`}
                />
              </div>
            </div>

            {/* Impact Metrics — comma or "·" separated */}
            <div className="mt-4">
              <Label className="font-mono-label text-stone-300">Impact Metrics (optional)</Label>
              <input
                type="text"
                value={draft.metrics}
                onChange={(e) => setDraft({ ...draft, metrics: e.target.value })}
                placeholder='e.g. "50% lift · 3 SKUs · 6 weeks · Sold out in 1 week"'
                data-testid={`case-study-metrics-${client.slug}`}
                className="mt-1.5 w-full bg-[#141414] border border-stone-800 rounded px-3 py-2 text-stone-100 focus:border-[#F62493] focus:outline-none"
              />
              <p className="font-mono-label text-stone-300 text-[10px] mt-1">
                Separate each stat with " · " (or commas). Displayed as bold pills below your case study.
              </p>
            </div>

            {/* Live preview — mirrors the client detail page rendering */}
            {(draft.before || draft.after || draft.metrics.trim()) && (
              <div className="mt-6" data-testid={`case-study-preview-${client.slug}`}>
                <p className="font-mono-label text-stone-300 mb-3 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F62493] animate-pulse" />
                  Live preview — this is exactly what visitors will see
                </p>
                <div className="bg-[#0A0A0A] border border-stone-800 rounded-sm p-6">
                  <p className="font-mono-label text-[#F62493] mb-5 text-xs">The Story · Before → After</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {draft.before ? (
                      <div className="relative bg-[#141414] border border-stone-800 rounded-sm p-5">
                        <span className="absolute top-3 right-3 font-mono-label text-stone-300 text-[9px]">01</span>
                        <p className="font-mono-label text-stone-300 mb-2 text-[10px]">Before — The Challenge</p>
                        <p className="font-display text-lg md:text-xl text-stone-100 leading-snug whitespace-pre-line">
                          {draft.before}
                        </p>
                      </div>
                    ) : (
                      <div className="border border-dashed border-stone-800 rounded-sm p-5 flex items-center justify-center text-stone-300 font-mono-label text-xs italic">
                        Before card hidden — no content
                      </div>
                    )}
                    {draft.after ? (
                      <div className="relative bg-[#1F1A0F] border-2 border-[#F62493] rounded-sm p-5">
                        <span className="absolute top-3 right-3 font-mono-label text-[#F62493] text-[9px]">02</span>
                        <p className="font-mono-label text-[#F62493] mb-2 text-[10px]">After — The Impact</p>
                        <p className="font-display text-lg md:text-xl text-stone-100 leading-snug whitespace-pre-line">
                          {draft.after}
                        </p>
                      </div>
                    ) : (
                      <div className="border border-dashed border-stone-800 rounded-sm p-5 flex items-center justify-center text-stone-300 font-mono-label text-xs italic">
                        After card hidden — no content
                      </div>
                    )}
                  </div>
                  {draft.metrics.trim() && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {draft.metrics
                        .split(/[·|\n,]/)
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((m, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center rounded-full bg-[#F62493] text-white px-3.5 py-1.5 font-mono-label text-xs"
                          >
                            {m}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() =>
                onAskDelete({ type: "client", slug: client.slug, name: client.name })
              }
              className="inline-flex items-center gap-2 text-red-700 hover:text-red-800 font-mono-label text-xs"
            >
              <Trash2 size={14} /> Delete this client
            </button>
            <button
              onClick={saveMeta}
              disabled={!isDirty || savingMeta}
              className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-5 py-2 disabled:opacity-50 hover:bg-[#F62493] transition-colors"
            >
              <Save size={14} /> {savingMeta ? "Saving…" : "Save text"}
            </button>
          </div>

          {/* Gallery management */}
          <ProjectsManager
            client={client}
            token={token}
            reload={reload}
            onAskDelete={onAskDelete}
          />
        </div>
      )}
    </div>
  );
};

// ---------- Projects manager (grid + add) ----------

const ProjectsManager = ({ client, token, reload, onAskDelete }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const makeCover = async (imgUrl) => {
    try {
      await axios.patch(`${API}/admin/clients/${client.slug}`, { cover: imgUrl }, { headers });
      toast.success("Cover updated");
      reload();
    } catch {
      toast.error("Could not update cover");
    }
  };

  const persistImageOrder = async (newList) => {
    try {
      await axios.post(
        `${API}/admin/clients/${client.slug}/projects/reorder`,
        { order: newList.map((p) => p.id) },
        { headers }
      );
      toast.success("Image order updated");
      reload();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const handleImgDragStart = (id) => setDragId(id);
  const handleImgDragOver = (e, id) => {
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };
  const handleImgDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };
  const handleImgDrop = (targetId) => {
    if (!dragId || dragId === targetId) {
      handleImgDragEnd();
      return;
    }
    const list = [...(client.projects || [])];
    const fromIdx = list.findIndex((p) => p.id === dragId);
    const toIdx = list.findIndex((p) => p.id === targetId);
    if (fromIdx < 0 || toIdx < 0) {
      handleImgDragEnd();
      return;
    }
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    handleImgDragEnd();
    persistImageOrder(list);
  };

  return (
    <div className="pt-4 border-t border-stone-800">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-display text-xl text-stone-100">Project images</h4>
          <p className="font-mono-label text-stone-300 mt-1">
            {(client.projects || []).length} pieces · Click an image to set as cover or delete · Drag to reorder
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-2 rounded-full border border-stone-400 text-stone-200 px-4 py-2 font-mono-label text-xs hover:bg-[#1A1A1A] transition-colors"
        >
          <Plus size={14} /> {showAdd ? "Hide form" : "Add image"}
        </button>
      </div>

      {showAdd && (
        <AddProjectForm
          slug={client.slug}
          token={token}
          onAdded={() => {
            setShowAdd(false);
            reload();
          }}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-5">
        {(client.projects || []).map((p) => {
          const isCover = client.cover === p.image;
          const isBeingDragged = dragId === p.id;
          const isDropTarget = dragOverId === p.id && dragId && dragId !== p.id;
          return (
            <div
              key={p.id}
              draggable
              onDragStart={() => handleImgDragStart(p.id)}
              onDragOver={(e) => handleImgDragOver(e, p.id)}
              onDrop={() => handleImgDrop(p.id)}
              onDragEnd={handleImgDragEnd}
              data-testid={`project-tile-${p.id}`}
              className={`relative group aspect-square overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${
                isBeingDragged ? "opacity-50 scale-95" : ""
              } ${
                isDropTarget
                  ? "border-[#F62493] ring-2 ring-[#F62493]"
                  : isCover
                  ? "border-[#F62493]"
                  : "border-stone-800"
              }`}
            >
              <img
                src={resolveImg(p.image)}
                alt={p.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#171717]/0 group-hover:bg-[#171717]/60 transition-colors duration-200 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {!isCover && (
                  <button
                    onClick={() => makeCover(p.image)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] text-stone-100 font-mono-label text-[10px] rounded-full hover:bg-[#F62493] hover:text-[#F7F3ED] transition-colors"
                  >
                    <Star size={11} /> Make cover
                  </button>
                )}
                <button
                  onClick={() =>
                    onAskDelete({ type: "project", slug: client.slug, projectId: p.id })
                  }
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white font-mono-label text-[10px] rounded-full hover:bg-red-700 transition-colors"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
              {isCover && (
                <span className="absolute top-1.5 left-1.5 px-2 py-0.5 bg-[#F62493] text-[#F7F3ED] font-mono-label text-[9px] rounded-full">
                  Cover
                </span>
              )}
              <span className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent text-[#F7F3ED] font-mono-label text-[10px] truncate">
                {p.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Add project form ----------

const AddProjectForm = ({ slug, token, onAdded }) => {
  const [mode, setMode] = useState("upload"); // upload | url
  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.url);
      toast.success("Image uploaded — fill in title & type, then click Add");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title || !type || !imageUrl) {
      toast.error("Need title, type, and an image");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/clients/${slug}/projects`, {
        title, type, image: imageUrl,
      }, { headers });
      toast.success("Image added");
      setTitle(""); setType(""); setImageUrl("");
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-[#141414] border border-stone-800 rounded p-5 mb-4 space-y-4"
    >
      <div className="flex items-center gap-3">
        <span className="font-mono-label text-stone-300">Source</span>
        <div className="inline-flex border border-stone-800 rounded-full overflow-hidden">
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`px-3 py-1.5 font-mono-label text-[10px] transition-colors ${
              mode === "upload" ? "bg-[#171717] text-[#F7F3ED]" : "text-stone-200 hover:bg-[#1A1A1A]"
            }`}
          >
            <Upload size={11} className="inline mr-1" /> Upload file
          </button>
          <button
            type="button"
            onClick={() => setMode("url")}
            className={`px-3 py-1.5 font-mono-label text-[10px] transition-colors ${
              mode === "url" ? "bg-[#171717] text-[#F7F3ED]" : "text-stone-200 hover:bg-[#1A1A1A]"
            }`}
          >
            <LinkIcon size={11} className="inline mr-1" /> Paste URL
          </button>
        </div>
      </div>

      {mode === "upload" ? (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-stone-800 rounded p-6 text-center hover:border-[#F62493] transition-colors"
        >
          {imageUrl ? (
            <div className="flex flex-col items-center gap-3">
              <img src={resolveImg(imageUrl)} alt="" className="max-h-32 rounded" />
              <button
                type="button"
                onClick={() => setImageUrl("")}
                className="font-mono-label text-[10px] text-stone-300 hover:text-red-700"
              >
                Choose a different image
              </button>
            </div>
          ) : (
            <div>
              <Upload size={20} className="mx-auto text-stone-300 mb-2" />
              <p className="text-stone-200">
                Drag &amp; drop an image here, or
                {" "}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="text-[#F62493] underline"
                >
                  click to browse
                </button>
              </p>
              <p className="font-mono-label text-stone-300 mt-2">
                JPG, PNG, WEBP, GIF · Max 15 MB
              </p>
              {uploading && <p className="text-stone-200 mt-2">Uploading…</p>}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <Label className="font-mono-label text-stone-300">Public image URL</Label>
          <Input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            className="mt-1.5"
          />
          {imageUrl && (
            <img
              src={resolveImg(imageUrl)}
              alt=""
              onError={(e) => (e.currentTarget.style.display = "none")}
              className="mt-3 max-h-32 rounded border border-stone-800"
            />
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="font-mono-label text-stone-300">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Spring Campaign Poster"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="font-mono-label text-stone-300">Type</Label>
          <Input
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="e.g. Print · Social · Packaging"
            className="mt-1.5"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving || !title || !type || !imageUrl}
        className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-6 py-2.5 hover:bg-[#F62493] disabled:opacity-50 transition-colors"
      >
        <Plus size={14} /> {saving ? "Adding…" : "Add image"}
      </button>
    </form>
  );
};

// ---------- New Client dialog ----------

const NewClientDialog = ({ open, onOpenChange, token, onCreated }) => {
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [summary, setSummary] = useState("");
  const [featured, setFeatured] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) {
      setName(""); setIndustry(""); setSummary(""); setFeatured(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !industry || !summary) {
      toast.error("Fill in name, industry, and a short summary.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/clients`,
        { name, industry, summary, featured },
        { headers }
      );
      toast.success(`${name} added — now upload their images.`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0A0A0A]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add a new client</DialogTitle>
          <DialogDescription>
            Create a new client section. You can add images and change the cover after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-mono-label text-stone-300">Client name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Herowear"
              className="mt-1.5"
              autoFocus
            />
          </div>
          <div>
            <Label className="font-mono-label text-stone-300">Industry / tagline *</Label>
            <Input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g. Athletic Apparel"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="font-mono-label text-stone-300">Summary *</Label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              placeholder="One or two sentences about the work you did for this client."
              className="mt-1.5"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFeatured(!featured)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-label text-xs transition-colors ${
                featured
                  ? "bg-[#F62493] text-[#F7F3ED]"
                  : "border border-stone-400 text-stone-200 hover:bg-[#1A1A1A]"
              }`}
            >
              {featured ? <Star size={14} fill="currentColor" /> : <StarOff size={14} />}
              {featured ? "Featured on homepage" : "Not featured"}
            </button>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-stone-400 text-stone-200 px-5 py-2 hover:bg-[#1A1A1A] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-5 py-2 hover:bg-[#F62493] disabled:opacity-50 transition-colors"
            >
              <UserPlus size={14} /> {saving ? "Creating…" : "Create client"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Studio;
